import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { createOtherEntry, updateOtherEntry } from "./otherEntryApi";

const emptyDraft = { name: "", agreedAmount: "", cost: "", discount: "" };

const draftFromEntry = (entry) => ({
  name: entry?.module ?? entry?.name ?? "",
  agreedAmount: entry?.agreedAmount ?? "",
  cost: entry?.cost ?? "",
  discount: entry?.discount ?? "",
});

/**
 * Raise a custom charge — the "Others" module. Money the agency earns on it (commission, bonus) is
 * not here on purpose: that is set and claimed from Direct Account by whoever holds those keys.
 *
 * Pass `initialEntry` (an account row) to edit it instead of creating one.
 */
const AddOtherEntryDialog = ({ open, clientId, initialEntry = null, onClose, onCreated }) => {
  const isEdit = Boolean(initialEntry?.id);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(isEdit ? draftFromEntry(initialEntry) : emptyDraft);
  }, [open, isEdit, initialEntry]);

  const change = (field) => (e) => setDraft((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.warning("Say what the charge is for.");
      return;
    }
    if (!isEdit && !clientId) {
      toast.error("No client to add this entry to.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        agreedAmount: draft.agreedAmount,
        cost: draft.cost,
        discount: draft.discount,
      };
      const saved = isEdit
        ? await updateOtherEntry(initialEntry.id, payload)
        : await createOtherEntry(clientId, payload);
      toast.success(
        isEdit ? "Entry updated." : "Entry added. Record its payment to send it for verification."
      );
      onCreated?.(saved);
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          (isEdit ? "Failed to update the entry." : "Failed to add the entry.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit custom entry" : "Add custom entry"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            For a charge no other module covers. Payments you record on it go through the same
            verification flow as any other module.
          </Typography>
          <TextField
            label="What is this for?"
            placeholder="e.g. Document translation"
            value={draft.name}
            onChange={change("name")}
            autoFocus
            fullWidth
            required
          />
          <TextField
            label="Agreed"
            type="number"
            value={draft.agreedAmount}
            onChange={change("agreedAmount")}
            fullWidth
          />
          <TextField
            label="Agent cost"
            type="number"
            value={draft.cost}
            onChange={change("cost")}
            fullWidth
          />
          <TextField
            label="Discount"
            type="number"
            value={draft.discount}
            onChange={change("discount")}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? (isEdit ? "Saving…" : "Adding…") : isEdit ? "Save changes" : "Add entry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOtherEntryDialog;
